import {
  selectAutoScrollEnabled,
  selectLocationEnabled,
  selectLocationPermission,
  selectMessageHistoryLimit,
  setAutoScrollEnabled,
  setLocationEnabled,
  setLocationPermission,
  setMessageHistoryLimit,
} from "@/features/settings/settingsSlice";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SettingsDialog = ({ open, onOpenChange }: SettingsDialogProps) => {
  const dispatch = useAppDispatch();
  const locationEnabled = useAppSelector(selectLocationEnabled);
  const locationPermission = useAppSelector(selectLocationPermission);
  const autoScrollEnabled = useAppSelector(selectAutoScrollEnabled);
  const messageHistoryLimit = useAppSelector(selectMessageHistoryLimit);

  const [pendingLocation, setPendingLocation] = useState(locationEnabled);
  const [pendingAutoScroll, setPendingAutoScroll] = useState(autoScrollEnabled);
  const [pendingHistoryLimit, setPendingHistoryLimit] = useState<number | null>(messageHistoryLimit);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  // Sync local state when dialog opens
  useEffect(() => {
    if (open) {
      setPendingLocation(locationEnabled);
      setPendingAutoScroll(autoScrollEnabled);
      setPendingHistoryLimit(messageHistoryLimit);
      setPermissionError(null);
    }
  }, [open, locationEnabled, autoScrollEnabled, messageHistoryLimit]);

  const handleCancel = () => {
    onOpenChange(false);
  };

  const saveAutoScroll = () => {
    if (pendingAutoScroll !== autoScrollEnabled) {
      dispatch(setAutoScrollEnabled(pendingAutoScroll));
    }
  };

  const saveHistoryLimit = () => {
    if (pendingHistoryLimit !== messageHistoryLimit) {
      dispatch(setMessageHistoryLimit(pendingHistoryLimit));
    }
  };

  const handleSave = async () => {
    saveAutoScroll();
    saveHistoryLimit();

    // Disabling location — no permission needed
    if (!pendingLocation) {
      dispatch(setLocationEnabled(false));
      onOpenChange(false);
      return;
    }

    // No change
    if (pendingLocation === locationEnabled) {
      onOpenChange(false);
      return;
    }

    // Enabling location
    if (!("geolocation" in navigator)) {
      setPermissionError("Geolocation is not supported by your browser.");
      return;
    }

    if ("permissions" in navigator) {
      const result = await navigator.permissions.query({ name: "geolocation" });
      if (result.state === "denied") {
        setPermissionError(
          "Location access was denied. Please enable it in your browser settings."
        );
        dispatch(setLocationPermission("denied"));
        return;
      }
      if (result.state === "granted") {
        dispatch(setLocationPermission("granted"));
        dispatch(setLocationEnabled(true));
        onOpenChange(false);
        return;
      }
    }

    // Permission state is "prompt" — close first so Radix's focus trap doesn't
    // block the rest of the app while the browser permission prompt is pending.
    onOpenChange(false);
    navigator.geolocation.getCurrentPosition(
      () => {
        dispatch(setLocationPermission("granted"));
        dispatch(setLocationEnabled(true));
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          dispatch(setLocationPermission("denied"));
        } else {
          dispatch(setLocationPermission("granted"));
          dispatch(setLocationEnabled(true));
        }
      },
      { timeout: 10000 }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
          className="sm:max-w-md"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
        <DialogHeader>
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6 py-2">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="location-switch" className="text-sm font-medium">
                Share Location
              </Label>
              <Switch
                id="location-switch"
                checked={pendingLocation}
                onCheckedChange={(checked) => {
                  setPendingLocation(checked);
                  setPermissionError(null);
                }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Attach your location to messages you send.
            </p>
            {permissionError && (
              <p className="text-sm text-red-500 mt-1">{permissionError}</p>
            )}
            {!permissionError && locationPermission === "denied" && (
              <p className="text-sm text-red-500 mt-1">
                Location access was denied. Please enable it in your browser settings.
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <Label htmlFor="autoscroll-switch" className="text-sm font-medium">
                Auto-Scroll
              </Label>
              <Switch
                id="autoscroll-switch"
                checked={pendingAutoScroll}
                onCheckedChange={setPendingAutoScroll}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Follow new messages when at the bottom of a conversation.
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Message History</Label>
              <Select
                value={pendingHistoryLimit === null ? "all" : String(pendingHistoryLimit)}
                onValueChange={(v) => setPendingHistoryLimit(v === "all" ? null : Number(v))}
              >
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="100">Last 100</SelectItem>
                  <SelectItem value="200">Last 200</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              Number of messages to load when opening a conversation.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
